/**
 * Collapse-on-drag controller (sc-47423).
 *
 * dnd-kit's standalone manager owns the sensors (5px mouse threshold, 200ms
 * touch hold, Esc to cancel). Everything the coach sees while dragging lives
 * here: the collapsed layout (one `body` class), the ghost, the origin box,
 * the drop gap, edge auto-scroll and the FLIP animation. No React state is touched during a
 * drag, so nothing re-renders.
 *
 * The save is NOT done here. On a valid drop the dragged node is moved to the
 * chosen slot in the DOM and the owning list's registered drop handler runs
 * with the same `{ item, from, to, oldIndex, newIndex }` shape SortableJS
 * produced, so the existing reorder/superset/section logic is reused as is.
 */
import { DragDropManager, Draggable, PointerSensor } from "@dnd-kit/dom";
import i18n from "../i18n";
import {
  autoScrollSpeed,
  clampGrabOffset,
  DRAG_START_DISTANCE,
  findDropSlot,
  findGroupSlot,
  getGroupRange,
  isSameSlot,
  type RowKind,
  scrollStep,
  slotsInsideBlocks,
  TOUCH_CANCEL_DISTANCE,
  TOUCH_HOLD_MS,
} from "./collapse-drag";

export type DropEvent = {
  item: HTMLElement;
  from: HTMLElement;
  to: HTMLElement;
  oldIndex: number;
  newIndex: number;
  /** `commit: "index"` only: the node the row goes before (a header or a row), null = end. */
  slotNode?: HTMLElement | null;
  /** Section group drag: the header plus every row under it, in order. The DOM is left alone. */
  group?: HTMLElement[];
};

/** A dragged group (superset, complex or lone row) and what follows it. */
export type OptimisticMove = { ids: string[]; following: string[] };

/**
 * What a handler returns so the move can be shown before the save lands:
 * the group and where it ends up, or true for a lone row at the pointer slot.
 */
type DropHandler = (event: DropEvent) => boolean | OptimisticMove | undefined;

const DRAGGING_CLASS = "is-collapse-dragging";
const SOURCE_CLASS = "is-drag-source";
export const GROUP_HANDLE_CLASS = "section-separator__group-handle";
const COLLAPSE_MS = 190;
const EXPAND_MS = 240;
const CANCEL_SLIDE_MS = 200;
const EASING = "cubic-bezier(.2,.8,.2,1)";

/**
 * How a list is laid out. `commit: "dom"` moves the node to the slot and lets
 * the handler revert it (calendar edit mode, SortableJS contract); "index"
 * leaves the DOM alone and passes the slot index (day view, where one exercise
 * is two grid cells, so the node can't be moved on its own).
 */
export type ListConfig = {
  rows: string;
  draggables: string;
  /** Ancestor that is the drop area and gets highlighted; null = the list. */
  cell: string | null;
  /** Every element whose position is animated when the view collapses. */
  animated: string;
  commit: "dom" | "index";
  /** Grab handle inside a row. */
  handle: string;
  /** Every grid cell that makes up a row (day view: the row plus its metrics). */
  cells?: (row: HTMLElement) => HTMLElement[];
  /** Second handle on a section header that drags the header with its exercises. */
  groupHandle?: string;
  /** Closest-match selector for a list, and a stable key that survives a re-render. */
  listSelector: string;
  key: (list: HTMLElement) => string;
};

export const CALENDAR_LIST: ListConfig = {
  rows: ":scope > .exercisex",
  draggables: ":scope > .exercisex, :scope > .section-separator",
  cell: ".day-editor",
  animated: ".day-editor, .day-editor .exercisex",
  commit: "dom",
  handle: ".grabber",
  groupHandle: `.${GROUP_HANDLE_CLASS}`,
  listSelector: ".exercises",
  key: (list) => list.id,
};

/** Program builder: one list per workout, workouts spread across weeks. */
export const BUILDER_LIST: ListConfig = {
  rows: ":scope > li",
  draggables: ":scope > li, :scope > .section-separator",
  cell: ".workout",
  animated: ".Program-Editor .exercises-list > li",
  commit: "dom",
  handle: ".drag-icon",
  groupHandle: `.${GROUP_HANDLE_CLASS}`,
  listSelector: ".exercises-list",
  key: (list) =>
    `${list.getAttribute("data-week")}:${list.getAttribute("data-session")}`,
};

/** Calendar day view: one exercise is a row li plus a metrics li in a grid. */
export const DAY_VIEW_LIST: ListConfig = {
  rows: ":scope > li.Program-Editor-Exercise",
  // Headers are slots too (above or below one), but have no handle to drag.
  draggables:
    ":scope > li.Program-Editor-Exercise, :scope > .section-separator",
  cell: ".exercises-frame",
  animated: ".Calendar-Day .exercises-list > li",
  commit: "index",
  handle: ".grabber",
  cells: (row) => {
    const next = row.nextElementSibling;
    return next instanceof HTMLElement &&
      next.classList.contains("exercise-metrics-container")
      ? [row, next]
      : [row];
  },
  listSelector: ".exercises-list",
  key: () => "day",
};

/** A superset or complex in a list, as buildSupersets returns them. */
export type ExerciseBlock = { ids: string[]; isComplex: boolean };

const lists = new Map<
  HTMLElement,
  {
    handler: DropHandler;
    config: ListConfig;
    getBlocks: () => ExerciseBlock[];
  }
>();
let manager: DragDropManager | null = null;
// Capture listeners on window run before dnd-kit's own, so by the time a drag
// ends on release this is already false; after Esc it is still true.
let pointerIsDown = false;

const prefersReducedMotion = () =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

/** A list registers once; its handler is looked up by the drag's source list. */
export const registerDropList = (
  list: HTMLElement,
  handler: DropHandler,
  config: ListConfig = CALENDAR_LIST,
  getBlocks: () => ExerciseBlock[] = () => []
) => {
  lists.set(list, { handler, config, getBlocks });
  return () => {
    if (lists.get(list)?.handler === handler) lists.delete(list);
  };
};

const configOf = (list: HTMLElement) =>
  lists.get(list)?.config ?? CALENDAR_LIST;
const cellOf = (list: HTMLElement) => {
  const { cell } = configOf(list);
  return (cell && list.closest<HTMLElement>(cell)) || list;
};

const getManager = () => {
  if (manager) return manager;
  window.addEventListener(
    "pointerdown",
    () => {
      pointerIsDown = true;
    },
    true
  );
  for (const type of ["pointerup", "pointercancel"])
    window.addEventListener(
      type,
      () => {
        pointerIsDown = false;
      },
      true
    );
  manager = new DragDropManager({
    // No Feedback/AutoScroller/Cursor plugins: the ghost, scroll and cursor
    // are driven by the loop below. Keyboard dragging is out of scope in v1.
    plugins: [],
    sensors: [
      PointerSensor.configure({
        activationConstraints: (event) =>
          event.pointerType === "mouse"
            ? { distance: { value: DRAG_START_DISTANCE } }
            : {
                delay: {
                  value: TOUCH_HOLD_MS,
                  tolerance: TOUCH_CANCEL_DISTANCE,
                },
              },
      }),
    ],
  });
  manager.monitor.addEventListener("dragstart", (event) => {
    const element = event.operation.source?.element;
    if (element instanceof HTMLElement) begin(element, event.operation);
  });
  manager.monitor.addEventListener("dragend", (event) => {
    finish(event.canceled);
  });
  return manager;
};

/** Make `item` draggable by `handle`. Call the returned function on unmount. */
export const registerDraggable = (
  item: HTMLElement,
  handle: HTMLElement,
  group = false
) => {
  // The group handle is its own draggable (element = handle) so one section
  // header can start two different drags; begin() walks back up to the header.
  const draggable = new Draggable(
    group
      ? { id: `group:${item.dataset.sectionId}`, element: handle, handle }
      : { id: item.id || item.dataset.sectionId || "", element: item, handle },
    getManager()
  );
  return () => draggable.destroy();
};

/**
 * Register a drag handle for every row in `list` that has a `.grabber` and
 * drop the ones whose row is gone. Rows re-render freely, so callers run this
 * after every render instead of tying it to React refs.
 */
export const syncDraggables = (
  list: HTMLElement,
  registry: Map<HTMLElement, DraggableEntry>
) => {
  for (const [item, entry] of registry) {
    // The handle can be swapped out under a live row (quick edit hides it),
    // which leaves the drag bound to a node that is no longer on the page.
    if (!item.isConnected || !entry.handle.isConnected) {
      entry.destroy();
      registry.delete(item);
    }
  }
  for (const item of list.querySelectorAll<HTMLElement>(
    configOf(list).draggables
  )) {
    const handle = item.querySelector<HTMLElement>(configOf(list).handle);
    if (handle && !item.dataset.optimistic && !registry.has(item))
      registry.set(item, { handle, destroy: registerDraggable(item, handle) });
    const { groupHandle } = configOf(list);
    const grip = groupHandle
      ? item.querySelector<HTMLElement>(groupHandle)
      : null;
    if (grip && !registry.has(grip))
      registry.set(grip, {
        handle: grip,
        destroy: registerDraggable(item, grip, true),
      });
  }
};

export type DraggableEntry = { handle: HTMLElement; destroy: () => void };

type Session = {
  item: HTMLElement;
  /** Section group drag: header plus its exercises. Null for a single node. */
  group: HTMLElement[] | null;
  /** Everything that moves on a drop elsewhere: the origin box goes around it. */
  moving: HTMLElement[];
  box: HTMLElement;
  gap: HTMLElement;
  from: HTMLElement;
  scroller: HTMLElement | null;
  releaseAnchor: () => void;
  grabOffset: number;
  pointer: { x: number; y: number };
  frame: number;
  lastTime: number;
  ghost: HTMLElement;
  target: { list: HTMLElement; index: number; same: boolean } | null;
};

let session: Session | null = null;

const isScrollable = (element: HTMLElement) => {
  const { overflowY } = getComputedStyle(element);
  return (
    /(auto|scroll)/.test(overflowY) &&
    element.scrollHeight > element.clientHeight
  );
};

/** The view's own scroller, or null when the page itself scrolls. */
const findScroller = (start: HTMLElement) => {
  for (let node = start.parentElement; node; node = node.parentElement) {
    if (node === document.body) return null;
    if (isScrollable(node)) return node;
  }
  return null;
};

const allLists = () =>
  Array.from(lists.keys()).filter((list) => list.isConnected);

/**
 * Rows a drop can land between: exercises, plus section headers when
 * `withSeparators`. Hit-testing and inserting use them so a header is a slot
 * of its own (above or below it) and the line never cuts through it.
 */
const dropTargets = (list: HTMLElement, withSeparators: boolean) =>
  Array.from(
    list.querySelectorAll<HTMLElement>(
      withSeparators ? configOf(list).draggables : configOf(list).rows
    )
  );

type Measured = Map<HTMLElement, DOMRect>;

const measure = (): Measured => {
  const rects: Measured = new Map();
  const selector = Array.from(
    new Set(allLists().map((l) => configOf(l).animated))
  );
  if (!selector.length) return rects;
  for (const element of document.querySelectorAll<HTMLElement>(
    selector.join(", ")
  ))
    rects.set(element, element.getBoundingClientRect());
  return rects;
};

/**
 * FLIP: animate transform/opacity from the measured rects to the current
 * layout. Cards are offset by their day's own movement so the nested
 * transforms don't add up. Only elements near the viewport animate, and
 * anything that moved more than a screen fades in instead of flying across.
 */
const flip = (before: Measured, duration: number) => {
  if (prefersReducedMotion()) return;
  const viewHeight = window.innerHeight;
  const dayShift = new Map<Element, { x: number; y: number }>();
  const moves: [HTMLElement, number, number, boolean][] = [];

  for (const [element, oldRect] of before) {
    if (!element.isConnected) continue;
    const newRect = element.getBoundingClientRect();
    const dx = oldRect.left - newRect.left;
    const dy = oldRect.top - newRect.top;
    const inView =
      newRect.bottom > -40 &&
      newRect.top < viewHeight + 40 &&
      (oldRect.bottom > -40 || oldRect.top < viewHeight + 40);
    if (element.classList.contains("day-editor"))
      dayShift.set(element, { x: dx, y: dy });
    if (!inView || (!dx && !dy)) continue;
    moves.push([element, dx, dy, Math.abs(dy) > viewHeight]);
  }

  for (const [element, dx, dy, fade] of moves) {
    const day = element.closest(".day-editor");
    const parent = day && day !== element ? dayShift.get(day) : { x: 0, y: 0 };
    const x = dx - (parent?.x ?? 0);
    const y = dy - (parent?.y ?? 0);
    element.animate(
      fade
        ? [{ opacity: 0 }, { opacity: 1 }]
        : [{ transform: `translate(${x}px, ${y}px)` }, { transform: "none" }],
      { duration, easing: EASING }
    );
  }
};

/** Scroll so `element`'s top lands on `top` (viewport Y). */
const anchor = (
  element: HTMLElement,
  top: number,
  scroller: HTMLElement | null
) => {
  const delta = element.getBoundingClientRect().top - top;
  if (scroller) scroller.scrollTop += delta;
  else window.scrollBy(0, delta);
};

/**
 * `anchor`, plus the room it needs when the browser clamps the scroll: the
 * collapse shortens the page, so a row near the top (or bottom) can't be
 * scrolled back under the pointer. The shortfall is added as padding on the
 * scroller until the drag ends; the returned function removes it.
 */
const holdAnchor = (
  element: HTMLElement,
  top: number,
  scroller: HTMLElement | null
) => {
  anchor(element, top, scroller);
  const shortfall = element.getBoundingClientRect().top - top;
  if (Math.abs(shortfall) < 1) return () => {};

  const host = scroller ?? document.body;
  const side = shortfall < 0 ? "paddingTop" : "paddingBottom";
  const previous = host.style[side];
  host.style[side] = `${
    (Number.parseFloat(getComputedStyle(host)[side]) || 0) + Math.abs(shortfall)
  }px`;
  anchor(element, top, scroller);
  return () => {
    host.style[side] = previous;
  };
};

const nameOf = (item: HTMLElement) =>
  item.querySelector(".name-order, .name-wrapper p")?.textContent?.trim() ||
  item.textContent?.trim().slice(0, 60) ||
  "";

const label = (key: string, options?: Record<string, unknown>) =>
  i18n.t(`athlete-portal:exerciseEditor.${key}`, options);

const GHOST_EXERCISES_SHOWN = 3;

type GhostContent = {
  icon: string;
  title: string;
  count: string;
  rows: HTMLElement[];
};

const createGhost = ({ icon, title, count, rows }: GhostContent) => {
  const ghost = document.createElement("div");
  ghost.className = "collapse-drag-ghost";
  const line = (parent: HTMLElement, className: string, text: string) => {
    const element = document.createElement("div");
    element.className = className;
    element.textContent = text;
    parent.appendChild(element);
    return element;
  };
  const head = line(ghost, "collapse-drag-ghost__head", "");
  const image = document.createElement("img");
  image.src = icon;
  image.alt = "";
  head.appendChild(image);
  line(head, "collapse-drag-ghost__title", title);
  if (count) line(head, "collapse-drag-ghost__count", count);
  for (const row of rows.slice(0, GHOST_EXERCISES_SHOWN))
    line(ghost, "collapse-drag-ghost__row", nameOf(row));
  const more = rows.length - GHOST_EXERCISES_SHOWN;
  if (more > 0)
    line(
      ghost,
      "collapse-drag-ghost__more",
      label("sectionExercisesMore", { n: more })
    );
  document.body.appendChild(ghost);
  return ghost;
};

const groupExercises = (group: HTMLElement[]) =>
  group.filter((node) => !node.classList.contains("section-separator"));

const MOVE_ICON = "/img/new-icons/drag-outline.svg";
const SECTION_ICON = "/img/new-icons/menu.svg";

/** The ghost's content and the origin box's label, per drag type. */
const describeDrag = (
  item: HTMLElement,
  group: HTMLElement[] | null,
  block: { rows: HTMLElement[]; isComplex: boolean } | null
) => {
  if (group) {
    const rows = groupExercises(group);
    return {
      boxLabel: rows.length
        ? label("dragMovingSection", { count: rows.length })
        : label("dragMovingSectionOnly"),
      ghost: {
        icon: SECTION_ICON,
        title:
          item
            .querySelector(".section-separator__title")
            ?.textContent?.trim() || "",
        count: label("dragExerciseCount", { count: rows.length }),
        rows,
      },
    };
  }
  if (block)
    return {
      boxLabel: label(
        block.isComplex ? "dragMovingComplex" : "dragMovingSuperset"
      ),
      ghost: {
        icon: MOVE_ICON,
        title: label(block.isComplex ? "dragComplex" : "dragSuperset"),
        count: label("dragExerciseCount", { count: block.rows.length }),
        rows: block.rows,
      },
    };
  if (item.classList.contains("section-separator"))
    return {
      boxLabel: label("dragMovingSectionOnly"),
      ghost: {
        icon: MOVE_ICON,
        title:
          item
            .querySelector(".section-separator__title")
            ?.textContent?.trim() || "",
        count: label("dragSectionOnly"),
        rows: [],
      },
    };
  return {
    boxLabel: label("dragMoving"),
    ghost: { icon: MOVE_ICON, title: nameOf(item), count: "", rows: [] },
  };
};

/** The section's rows on the page right now, header first. */
const groupNodes = (list: HTMLElement, header: HTMLElement) => {
  const targets = dropTargets(list, true);
  const kinds = targets.map(rowKind);
  const { start, end } = getGroupRange(kinds, targets.indexOf(header));
  return targets.slice(start, end);
};

const rowKind = (row: HTMLElement): RowKind =>
  row.classList.contains("section-separator") ? "section" : "exercise";

/** Multi-row supersets and complexes of `list`, as rows on the page. */
const blocksOf = (list: HTMLElement) => {
  const rows = dropTargets(list, true);
  return (lists.get(list)?.getBlocks() ?? [])
    .map(({ ids, isComplex }) => ({
      isComplex,
      rows: ids
        .map((id) => rows.find((row) => row.id === String(id)))
        .filter((row): row is HTMLElement => !!row),
    }))
    .filter(
      // A stale nextSuperset can point across a section header; the page
      // (and its A/B letters) already treats those rows as separate.
      (block) =>
        block.rows.length > 1 &&
        block.rows.every(
          (row, position) =>
            position === 0 ||
            rows.indexOf(row) === rows.indexOf(block.rows[position - 1]) + 1
        )
    );
};

/** One dashed box around what is moving, so rows don't need a wrapper. */
const createOriginBox = (text: string) => {
  const box = document.createElement("div");
  box.className = "collapse-drag-origin";
  const tag = document.createElement("span");
  tag.className = "collapse-drag-origin__label";
  tag.textContent = text;
  box.appendChild(tag);
  document.body.appendChild(box);
  return box;
};

const ORIGIN_BOX_OUTSET = 3;

const updateOriginBox = (box: HTMLElement, nodes: HTMLElement[]) => {
  const rects = nodes
    .map((node) => node.getBoundingClientRect())
    .filter((rect) => rect.height > 0);
  if (!rects.length) return;
  // Day view rows are full-width cards: outset would poke past their edges.
  const outset = nodes[0]?.closest(".Calendar-Day") ? 0 : ORIGIN_BOX_OUTSET;
  const top = Math.min(...rects.map((rect) => rect.top)) - outset;
  const bottom = Math.max(...rects.map((rect) => rect.bottom)) + outset;
  const left = Math.min(...rects.map((rect) => rect.left)) - outset;
  const right = Math.max(...rects.map((rect) => rect.right)) + outset;
  box.style.transform = `translate3d(${left}px, ${top}px, 0)`;
  box.style.width = `${right - left}px`;
  box.style.height = `${bottom - top}px`;
};

/**
 * The drop target is a real element in the list, so the rows below it make
 * room. It is out of the list again before any drop handler or re-render runs.
 */
const createGap = (sectionOnly: boolean) => {
  const gap = document.createElement("div");
  gap.className = `collapse-drag-gap${sectionOnly ? " is-compact" : ""}`;
  gap.textContent = label(
    sectionOnly ? "dragSectionStartsHere" : "dragDropHere"
  );
  return gap;
};

/** The node a row inserted at `index` goes before (null = end of the list). */
const slotReference = (list: HTMLElement, index: number): Node | null => {
  const targets = dropTargets(list, true);
  if (targets[index]) return targets[index];
  const last = targets[targets.length - 1];
  if (!last) return null;
  const cells = cellsOf(list, last);
  return cells[cells.length - 1].nextSibling;
};

function begin(
  element: HTMLElement,
  operation: { position: { current: { x: number; y: number } } }
) {
  if (session) return;
  const isGroup = element.classList.contains(GROUP_HANDLE_CLASS);
  const item = isGroup
    ? element.closest<HTMLElement>(".section-separator")
    : element;
  if (!item?.parentElement) {
    manager?.actions.stop({ canceled: true });
    return;
  }
  const group = isGroup ? groupNodes(item.parentElement, item) : null;
  const block = group
    ? null
    : (blocksOf(item.parentElement).find(({ rows }) => rows.includes(item)) ??
      null);
  const moving = group ?? block?.rows ?? [item];
  // A previous drop is still saving; its copy isn't a real row to drag.
  if (document.querySelector("[data-optimistic]")) {
    manager?.actions.stop({ canceled: true });
    return;
  }
  const pointer = { ...operation.position.current };
  const scroller = findScroller(item);
  const grabOffset = clampGrabOffset(
    pointer.y,
    item.getBoundingClientRect().top
  );
  const { boxLabel, ghost } = describeDrag(item, group, block);
  const before = measure();

  document.body.classList.add(DRAGGING_CLASS);
  for (const node of moving) node.classList.add(SOURCE_CLASS);
  const releaseAnchor = holdAnchor(item, pointer.y - grabOffset, scroller);
  flip(before, COLLAPSE_MS);
  if (navigator.vibrate && matchMedia("(pointer: coarse)").matches)
    navigator.vibrate(8);

  session = {
    item,
    group,
    moving,
    box: createOriginBox(boxLabel),
    gap: createGap(!group && item.classList.contains("section-separator")),
    from: item.parentElement as HTMLElement,
    scroller,
    releaseAnchor,
    grabOffset,
    pointer,
    frame: 0,
    lastTime: performance.now(),
    ghost: createGhost(ghost),
    target: null,
  };

  window.addEventListener("pointermove", onPointerMove, true);
  window.addEventListener("blur", onCancel);
  window.addEventListener("pointercancel", onCancel);
  session.frame = requestAnimationFrame(tick);
}

/**
 * Releasing over a list fires a `click` on it, which the day's own handler
 * reads as "open quick edit". A drag is never a click, so eat the next one.
 * After Esc the pointer is still down, so wait for its release first.
 */
const swallowNextClick = (pointerStillDown: boolean) => {
  const eat = (event: Event) => {
    event.stopPropagation();
    event.preventDefault();
  };
  const remove = () => window.removeEventListener("click", eat, true);
  window.addEventListener("click", eat, true);
  if (!pointerStillDown) {
    setTimeout(remove, 0);
    return;
  }
  const afterRelease = () => setTimeout(remove, 0);
  window.addEventListener("pointerup", afterRelease, {
    capture: true,
    once: true,
  });
  window.addEventListener("pointercancel", afterRelease, {
    capture: true,
    once: true,
  });
};

const OPTIMISTIC_TIMEOUT_MS = 10000;
const SETTLE_MS = 200;

type Optimistic = { copies: HTMLElement[]; hidden: HTMLElement[] };

/** Static copies go before `reference`; the originals hide until the save. */
const showCopies = (
  hidden: HTMLElement[],
  list: HTMLElement,
  reference: Node | null
): Optimistic => {
  const copies = hidden.map((row) => {
    const copy = row.cloneNode(true) as HTMLElement;
    copy.removeAttribute("id");
    for (const node of copy.querySelectorAll("[id]"))
      node.removeAttribute("id");
    copy.dataset.optimistic = "true";
    list.insertBefore(copy, reference);
    return copy;
  });
  for (const row of hidden) row.style.display = "none";
  return { copies, hidden };
};

const cellsOf = (list: HTMLElement, row: HTMLElement) =>
  configOf(list).cells?.(row) ?? [row];

const childrenOf = (list: HTMLElement) =>
  Array.from(list.children).filter(
    (child): child is HTMLElement => child instanceof HTMLElement
  );

/** Rows have an exercise id; a section header is keyed `section:<id>`. */
export const rowKey = (row: HTMLElement) =>
  row.id || (row.dataset.sectionId ? `section:${row.dataset.sectionId}` : "");

/** Show a whole group where the server will put it, not at the pointer. */
const showGroup = (
  from: HTMLElement,
  list: HTMLElement,
  move: OptimisticMove
): Optimistic | null => {
  const source = childrenOf(from);
  const moved = move.ids
    .map((id) => source.find((row) => rowKey(row) === id))
    .filter((row): row is HTMLElement => !!row);
  if (!moved.length) return null;
  const inTarget = childrenOf(list);
  const before = move.following
    .map((id) =>
      inTarget.find((row) => rowKey(row) === id && !moved.includes(row))
    )
    .find(Boolean);
  // A section drag can end after a trailing empty header; an exercise can't.
  const rows = dropTargets(
    list,
    move.ids.some((id) => id.startsWith("section:"))
  );
  const lastRow = rows[rows.length - 1];
  const lastCells = lastRow ? cellsOf(list, lastRow) : [];
  const reference =
    before ?? lastCells[lastCells.length - 1]?.nextSibling ?? null;
  return showCopies(
    moved.flatMap((row) => cellsOf(from, row)),
    list,
    reference
  );
};

/**
 * The save re-renders the calendar. Wait for the dropped node to land, drop
 * the optimistic copy, and for a move to another day bring it into view (it
 * may be a week away) and flash it. If the save never lands (it failed), the
 * copy is removed and the original reappears where it was.
 */
const trackDrop = (
  item: HTMLElement,
  from: HTMLElement,
  to: HTMLElement,
  optimistic: Optimistic | null,
  isGroup = false,
  movedIds: string[] = []
) => {
  const config = configOf(to);
  const listKey = config.key(to);
  const crossList = config.key(from) !== listKey;
  const landedIn = (element: HTMLElement) => {
    const list = element.closest<HTMLElement>(config.listSelector);
    return !!list && config.key(list) === listKey;
  };
  const sectionId = item.dataset.sectionId;
  const find = () =>
    (item.id ? document.getElementById(item.id) : null) ??
    (sectionId
      ? document.querySelector<HTMLElement>(
          `[data-section-id="${sectionId}"]:not([data-optimistic])`
        )
      : null);

  let settle: ReturnType<typeof setTimeout> | undefined;
  const finishTracking = (landed: HTMLElement | null) => {
    observer.disconnect();
    clearTimeout(timeout);
    clearTimeout(settle);
    for (const copy of optimistic?.copies ?? []) copy.remove();
    for (const row of optimistic?.hidden ?? []) row.style.display = "";
    if (landed)
      requestAnimationFrame(() =>
        reveal(
          landed,
          isGroup
            ? rowsUnder(landed)
            : movedIds
                .map((id) => document.getElementById(id))
                .filter((row): row is HTMLElement => !!row && row !== landed)
        )
      );
  };
  const timeout = setTimeout(() => finishTracking(null), OPTIMISTIC_TIMEOUT_MS);
  // Only the lists' own rows say the save re-rendered. The submit adds and
  // removes its hidden form on <body> within ~100ms, which would end the
  // tracking before the save lands and snap the rows back for seconds.
  const inLists = (node: Node) =>
    !from.isConnected ||
    !to.isConnected ||
    from.contains(node) ||
    to.contains(node);
  const observer = new MutationObserver((records) => {
    const foreign = records.some(
      (record) =>
        inLists(record.target) &&
        [...record.addedNodes, ...record.removedNodes].some(
          (node) => !optimistic?.copies.includes(node as HTMLElement)
        )
    );
    if (!foreign) return;
    if (crossList) {
      const element = find();
      if (element && element !== item && landedIn(element))
        finishTracking(element);
      return;
    }
    // Same day: React may reuse the node, so wait for the re-render to settle.
    clearTimeout(settle);
    settle = setTimeout(() => finishTracking(find()), SETTLE_MS);
  });
  observer.observe(document.body, { childList: true, subtree: true });
};

/** The rows after a section header, down to the next header. */
export const rowsUnder = (header: HTMLElement) => {
  const rows: HTMLElement[] = [];
  for (
    let node = header.nextElementSibling;
    node instanceof HTMLElement &&
    !node.classList.contains("section-separator");
    node = node.nextElementSibling
  )
    rows.push(node);
  return rows;
};

const reveal = (element: HTMLElement, rest: HTMLElement[] = []) => {
  const rect = element.getBoundingClientRect();
  if (rect.top < 0 || rect.bottom > window.innerHeight)
    element.scrollIntoView({
      block: "center",
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  if (!prefersReducedMotion())
    for (const node of [element, ...rest])
      node.animate(
        [
          { outline: "2px solid var(--gradient-left)" },
          { outline: "2px solid var(--gradient-left)", offset: 0.6 },
          { outline: "2px solid transparent" },
        ],
        { duration: 1200 }
      );
};

function onPointerMove(event: PointerEvent) {
  if (session) session.pointer = { x: event.clientX, y: event.clientY };
}

function onCancel() {
  manager?.actions.stop({ canceled: true });
}

/** Below-right of the pointer, so it never covers the origin or the drop gap. */
const GHOST_OFFSET_X = 16;
const GHOST_OFFSET_Y = 18;

/** One loop: auto-scroll, then ghost, then hit-test (never only on move). */
function tick(now: number) {
  if (!session) return;
  const current = session;
  const frameMs = now - current.lastTime;
  current.lastTime = now;
  const { x, y } = current.pointer;

  const bounds = current.scroller
    ? current.scroller.getBoundingClientRect()
    : { left: 0, right: window.innerWidth, top: 0, bottom: window.innerHeight };
  const dy = scrollStep(autoScrollSpeed(y, bounds.top, bounds.bottom), frameMs);
  const dx = scrollStep(autoScrollSpeed(x, bounds.left, bounds.right), frameMs);
  if (dx || dy) {
    if (current.scroller) {
      current.scroller.scrollTop += dy;
      current.scroller.scrollLeft += dx;
    } else window.scrollBy(dx, dy);
  }

  current.ghost.style.transform = `translate3d(${x + GHOST_OFFSET_X}px, ${y + GHOST_OFFSET_Y}px, 0)`;
  updateOriginBox(current.box, current.moving);
  hitTest(current);
  current.frame = requestAnimationFrame(tick);
}

function hitTest(current: Session) {
  const { x, y } = current.pointer;
  const list = allLists().find((candidate) => {
    const cell = cellOf(candidate);
    const rect = cell.getBoundingClientRect();
    return (
      x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
    );
  });

  if (!list) {
    clearTarget(current);
    return;
  }

  const targets = dropTargets(list, true);
  const listRect = list.getBoundingClientRect();
  const rects = targets.map((target) => {
    const rect = target.getBoundingClientRect();
    return { top: rect.top, height: rect.height };
  });
  const emptyTop = listRect.top + listRect.height / 2;
  if (current.group) {
    const own =
      list === current.from
        ? getGroupRange(targets.map(rowKind), targets.indexOf(current.item))
        : null;
    const groupSlot = findGroupSlot(
      rects,
      targets.map(rowKind),
      y,
      emptyTop,
      own
    );
    if (!groupSlot) {
      clearTarget(current);
      return;
    }
    showSlot(current, list, groupSlot.index, false);
    return;
  }
  // The dragged row's own superset stays open: dropping inside it reorders it.
  const insideOtherBlocks = slotsInsideBlocks(
    blocksOf(list)
      .filter(({ rows }) => !rows.includes(current.item))
      .map(({ rows }) => rows.map((row) => targets.indexOf(row)))
  );
  const slot = findDropSlot(rects, y, emptyTop, insideOtherBlocks);
  const sameList = list === current.from;
  const sourceIndex = targets.indexOf(current.item);
  const same = isSameSlot(sourceIndex, slot.index, sameList);

  showSlot(current, list, slot.index, same);
}

function clearTarget(current: Session) {
  current.target = null;
  current.gap.remove();
  for (const other of allLists())
    cellOf(other).classList.remove("is-drop-target");
}

function showSlot(
  current: Session,
  list: HTMLElement,
  index: number,
  same: boolean
) {
  // No gap on a no-op slot: the origin box is the indicator there.
  if (same) {
    clearTarget(current);
    current.target = { list, index, same };
    return;
  }
  const { target } = current;
  if (target?.list === list && target.index === index && !target.same) return;
  current.target = { list, index, same };
  list.insertBefore(current.gap, slotReference(list, index));
  for (const other of allLists())
    cellOf(other).classList.toggle("is-drop-target", other === list);
}

const indexAmong = (list: HTMLElement, item: HTMLElement) =>
  Array.from(
    list.querySelectorAll<HTMLElement>(configOf(list).draggables)
  ).indexOf(item);

const moveIds = (result: ReturnType<DropHandler>) =>
  result && result !== true ? result.ids : [];

/**
 * Hand the drop to the list's handler. Returns the optimistic copy when the
 * move can be shown right away.
 */
function commitDrop(
  current: Session,
  drop: NonNullable<Session["target"]>
): HTMLElement | null {
  const { item, from } = current;
  // Same contract SortableJS gave the handler: the node already sits in the
  // dropped slot, indexes count exercises and separators alike. The handler
  // reverts the DOM move itself and lets the server response drive the layout.
  const handler = lists.get(from)?.handler;
  if (current.group) {
    // A group is several nodes, so the DOM is left alone like the day view:
    // the handler gets the slot among the target's rows and works from data.
    const result = handler?.({
      item,
      from,
      to: drop.list,
      oldIndex: indexAmong(from, item),
      newIndex: drop.index,
      group: current.group,
    });
    const optimistic =
      result && result !== true ? showGroup(from, drop.list, result) : null;
    if (optimistic || drop.list !== from)
      trackDrop(item, from, drop.list, optimistic, true);
    return optimistic?.copies[0] ?? null;
  }
  if (configOf(from).commit === "index") {
    // Indexes count rows only; newIndex is the slot in the list as it was.
    // The slot itself also counts headers, so say which node it sits before.
    const rows = dropTargets(from, false);
    const targets = dropTargets(drop.list, true);
    const result = handler?.({
      item,
      from,
      to: drop.list,
      oldIndex: rows.indexOf(item),
      newIndex: targets
        .slice(0, drop.index)
        .filter((node) => rows.includes(node)).length,
      slotNode: targets[drop.index] ?? null,
    });
    const optimistic =
      result && result !== true ? showGroup(from, drop.list, result) : null;
    if (optimistic)
      trackDrop(item, from, drop.list, optimistic, false, moveIds(result));
    return optimistic?.copies[0] ?? null;
  }
  const oldIndex = indexAmong(from, item);
  const targets = dropTargets(drop.list, true);
  const anchorNode = targets[drop.index] ?? null;
  const last = targets[targets.length - 1];
  // Read before the insert below: it changes what follows the last row.
  const reference = anchorNode ?? last?.nextSibling ?? null;
  if (anchorNode) drop.list.insertBefore(item, anchorNode);
  else if (last) drop.list.insertBefore(item, last.nextSibling);
  else drop.list.appendChild(item);
  const newIndex = indexAmong(drop.list, item);

  let result: ReturnType<DropHandler>;
  try {
    result = handler?.({ item, from, to: drop.list, oldIndex, newIndex });
  } catch (error) {
    from.insertBefore(item, from.children[oldIndex] ?? null);
    throw error;
  }
  // The handler has put the node back in its source list by now.
  const optimistic =
    result === true
      ? showCopies(
          [item],
          drop.list,
          reference === item ? item.nextSibling : reference
        )
      : result
        ? showGroup(from, drop.list, result)
        : null;
  if (optimistic || drop.list !== from)
    trackDrop(item, from, drop.list, optimistic, false, moveIds(result));
  return optimistic?.copies[0] ?? null;
}

function finish(canceled: boolean) {
  if (!session) return;
  const current = session;
  session = null;
  cancelAnimationFrame(current.frame);
  window.removeEventListener("pointermove", onPointerMove, true);
  window.removeEventListener("blur", onCancel);
  window.removeEventListener("pointercancel", onCancel);

  swallowNextClick(pointerIsDown);
  const { item, target } = current;
  current.gap.remove();
  const ghostTop = current.ghost.getBoundingClientRect().top;
  const sourceTop = item.getBoundingClientRect().top;
  const drop = !canceled && target && !target.same ? target : null;

  const before = measure();
  // The move goes in while the view is still collapsed, so the expand below
  // can anchor the card where it landed. A move that can't be shown yet (a
  // group, or another day) leaves the view where it was instead of chasing a
  // row that hasn't moved.
  let copy: HTMLElement | null = null;
  try {
    copy = drop ? commitDrop(current, drop) : null;
  } finally {
    document.body.classList.remove(DRAGGING_CLASS);
    current.releaseAnchor();
    for (const node of current.moving) node.classList.remove(SOURCE_CLASS);
    current.box.remove();
    for (const cell of document.querySelectorAll(".is-drop-target"))
      cell.classList.remove("is-drop-target");
    if (copy) anchor(copy, ghostTop, current.scroller);
    else anchor(item, sourceTop, current.scroller);
    flip(before, EXPAND_MS);

    const removeOverlays = () => current.ghost.remove();
    if (!drop && !prefersReducedMotion()) {
      const rect = item.getBoundingClientRect();
      current.ghost
        .animate(
          [
            { transform: current.ghost.style.transform },
            {
              transform: `translate3d(${rect.left}px, ${rect.top}px, 0)`,
              opacity: 0.2,
            },
          ],
          { duration: CANCEL_SLIDE_MS, easing: "ease-in" }
        )
        .finished.then(removeOverlays, removeOverlays);
    } else removeOverlays();
  }
}
